import * as React from 'react';
import { MapPin, Star, Check } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGmapsSearch, useGmapsPlace, type GmapsPlace } from '@/lib/gmaps';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { cn } from '@/lib/cn';

/**
 * Google Maps Place picker — search → select → write to brief.gmaps +
 * brief.address.geo. Also pulls reviews (up to 5) into brief.gmaps.reviews_raw
 * which feeds the substring-grounded reviews.v1 section.
 */
export function GmapsPicker() {
  const [q, setQ] = React.useState('');
  const debouncedQ = useDebouncedValue(q, 300);
  const search = useGmapsSearch(debouncedQ);
  const placeMut = useGmapsPlace();
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const [status, setStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  const linkedPlaceId = brief?.integrations?.gmaps_place_id as string | undefined;
  const linkedName = brief?.gmaps?.maps_url ? brief?.company?.name : null;

  const onPick = async (place_id: string) => {
    setStatus('idle');
    try {
      const place = await placeMut.mutateAsync(place_id);
      const patch = buildBriefPatchFromPlace(place, brief);
      await update.mutateAsync({ patch });
      setStatus('saved');
      setQ('');
    } catch {
      setStatus('error');
    }
  };

  const onUnlink = async () => {
    setStatus('idle');
    try {
      await update.mutateAsync({
        patch: {
          integrations: { ...(brief?.integrations ?? {}), gmaps_place_id: undefined },
          gmaps: {
            ...(brief?.gmaps ?? {}),
            rating: undefined,
            reviews_count: undefined,
            maps_url: undefined,
            reviews_raw: undefined,
          },
        },
      });
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="size-5" aria-hidden="true" /> Google Maps
        </CardTitle>
        <CardDescription>
          Powiąż swoją wizytówkę w Google Maps — wyciągniemy ocenę, liczbę opinii i
          link na mapę. Opinie pojawią się w sekcji „Co mówią pacjenci" na publicznej
          stronie.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {linkedPlaceId ? (
          <div className="rounded-md bg-success/5 p-4 ring-1 ring-success/20">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-text">
                  <Check className="size-4 text-success" aria-hidden="true" />
                  Wizytówka połączona
                </p>
                <p className="mt-1 text-xs font-mono text-text-subtle">{linkedPlaceId}</p>
                {brief?.gmaps?.rating ? (
                  <p className="mt-2 text-sm text-text-muted">
                    <Star className="mr-1 inline size-3.5 text-warning" aria-hidden="true" />
                    {brief.gmaps.rating} · {brief.gmaps.reviews_count ?? 0} opinii
                  </p>
                ) : null}
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={onUnlink}>
                Odłącz
              </Button>
            </div>
            <p className="mt-3 text-xs text-text-subtle">
              Wyszukaj poniżej, aby zmienić powiązaną wizytówkę.
            </p>
          </div>
        ) : null}

        <div>
          <Input
            placeholder="Wpisz nazwę kliniki lub adres (min. 3 znaki)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {search.data && !search.data.configured ? (
            <p className="mt-2 text-xs text-warning">
              Google Places API nie jest skonfigurowany. Operator powiąże wizytówkę ręcznie.
            </p>
          ) : null}

          {q.trim().length >= 3 && search.data?.configured ? (
            <ul role="list" className="mt-2 divide-y divide-border rounded-md bg-surface ring-1 ring-black/5">
              {search.isPending ? (
                <li className="p-3 text-sm text-text-muted">Wyszukiwanie…</li>
              ) : search.data.predictions.length === 0 ? (
                <li className="p-3 text-sm text-text-muted">Brak wyników.</li>
              ) : (
                search.data.predictions.map((p) => (
                  <li key={p.place_id}>
                    <button
                      type="button"
                      onClick={() => onPick(p.place_id)}
                      className={cn(
                        'block w-full px-3 py-3 text-left text-sm hover:bg-bg',
                        placeMut.isPending && 'opacity-50'
                      )}
                      disabled={placeMut.isPending}
                    >
                      <p className="font-medium text-text">
                        {p.structured_formatting.main_text}
                      </p>
                      <p className="mt-0.5 text-xs text-text-muted">
                        {p.structured_formatting.secondary_text ?? p.description}
                      </p>
                    </button>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>

        {status === 'saved' ? (
          <p className="rounded-md bg-success/10 p-3 text-sm text-success ring-1 ring-success/20">
            Wizytówka powiązana. Po kolejnej budowie strony pojawi się ocena i opinie.
          </p>
        ) : status === 'error' ? (
          <p className="rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
            Nie udało się zapisać. Spróbuj ponownie.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Maps a Google Place to a patch object the brief endpoint accepts.
 * Carries: gmaps.{rating, reviews_count, maps_url, reviews_raw},
 * address.geo, integrations.gmaps_place_id, company.phone (when populated
 * and currently empty).
 */
function buildBriefPatchFromPlace(
  place: GmapsPlace,
  brief: Record<string, unknown> | undefined
): Record<string, unknown> {
  const reviewsRaw = (place.reviews ?? [])
    .filter((r) => r.text && r.text.length >= 20)
    .slice(0, 5)
    .map((r) => ({
      text: r.text,
      rating: r.rating,
      language: r.language,
    }));
  const patch: Record<string, unknown> = {
    integrations: {
      ...(((brief?.integrations as Record<string, unknown>) ?? {})),
      gmaps_place_id: place.place_id,
      google_business_profile_url: place.url,
    },
    gmaps: {
      ...((brief?.gmaps as Record<string, unknown>) ?? {}),
      rating: place.rating,
      reviews_count: place.user_ratings_total,
      maps_url: place.url,
      ...(reviewsRaw.length > 0 ? { reviews_raw: reviewsRaw } : {}),
    },
  };
  if (place.geometry?.location) {
    const existingAddress = (brief?.address as Record<string, unknown>) ?? {};
    patch['address'] = {
      ...existingAddress,
      geo: {
        lat: place.geometry.location.lat,
        lng: place.geometry.location.lng,
      },
    };
  }
  return patch;
}

function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}
